import { AuthProvider, signInWithPopup } from "firebase/auth";
import {
  initFirebase,
  clearFirebaseLocalStorage,
} from "@core/services/api/firebase";
import { apiRequest } from "@core/services/api/apiRequest";

export async function loginUserWithOAuth(
  provider: AuthProvider,
  onError?: () => void,
  _retried = false
) {
  const apiEndpoint = "/auth/login/";

  try {
    // Ensure firebase is initialized
    const auth = await initFirebase();

    // Sign in or register user with OAuth provider
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Get Firebase token
    const idToken = await user.getIdToken();

    // Prepare API request body
    const body = {
      firebase_token: idToken,
    };

    // Make API call to backend (requireAuth=false since user is logging in)
    const response = await apiRequest(apiEndpoint, "POST", body, false);

    return { authToken: idToken, ...response };
  } catch (error) {
    const msg = (error as Error).message || "";
    const authError = error as any;
    const email = authError.customData?.email || authError.email;

    // If an orphaned Firebase account from a different provider blocks OAuth,
    // try to clean it up via prevalidate and retry once.
    if (
      !_retried &&
      email &&
      msg.includes("auth/account-exists-with-different-credential")
    ) {
      let prevalidateResult;
      try {
        prevalidateResult = await apiRequest(
          "/auth/prevalidate/",
          "POST",
          { email },
          false
        );
      } catch {
        // Graceful degradation — prevalidate failed, fall through to error
      }

      if (prevalidateResult?.cleaned) {
        return loginUserWithOAuth(provider, onError, true);
      }
      if (prevalidateResult?.retry_after) {
        throw new Error("Please wait a few minutes and try again.");
      }
    }

    clearFirebaseLocalStorage();
    onError?.();
    throw error;
  }
}

export default loginUserWithOAuth;
